import { useMemo, useState } from 'react';
import { envLogoCandidates, resolveAppEnvironment } from '../appEnvironment';
import styles from './EnvBrandImage.module.css';

type EnvBrandImageProps = {
  /** Base logo path (e.g. the shared landing logo). Per-env variants are tried first. */
  baseSrc: string;
  alt: string;
  className?: string;
  draggable?: boolean;
  /** Show the environment label badge (LOCAL / DEV). Off for the production app. */
  showBadge?: boolean;
};

/** Standalone LOCAL/DEV pill — keep outside scaled/clipped logo frames. */
export function EnvBuildBadge({ className }: { className?: string }) {
  const env = resolveAppEnvironment();
  if (!env.label) {
    return null;
  }
  return (
    <span
      className={className ? `${styles.badge} ${className}` : styles.badge}
      style={{ ['--env-badge-color' as string]: env.color }}
    >
      {env.label}
    </span>
  );
}

/**
 * Renders the environment-specific logo when a matching file exists
 * (e.g. `offizz-landing-logo.dev.png`), falling back to the shared logo,
 * with an optional corner badge so builds are easy to tell apart.
 */
export function EnvBrandImage({
  baseSrc,
  alt,
  className,
  draggable = false,
  showBadge = true,
}: EnvBrandImageProps) {
  const candidates = useMemo(() => envLogoCandidates(baseSrc), [baseSrc]);
  const [index, setIndex] = useState(0);
  const env = resolveAppEnvironment();

  const img = (
    <img
      src={candidates[index]}
      alt={alt}
      className={className}
      draggable={draggable}
      onError={() => setIndex((i) => (i < candidates.length - 1 ? i + 1 : i))}
    />
  );

  if (!showBadge || !env.label) {
    return img;
  }

  return (
    <span className={styles.wrap} style={{ ['--env-badge-color' as string]: env.color }}>
      {img}
      <span className={styles.badge}>{env.label}</span>
    </span>
  );
}
